import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";

import CaptureSegmentDropContext from "./CaptureSegmentDropContext";
import { selectSegmentById, containsSegmentId } from "./selectSegmentById";
import useInsertCaptureIntoSegment from "../Capture/components/useInsertCaptureIntoSegment";
import usePendingCaptureInsert from "../Capture/components/usePendingCaptureInsert";
import {
  getWheelSegments,
  getSelectedSegment,
} from "../../redux/selectors/segmentBuilderSelectors";
import { setWheelSegments } from "../../redux/reducers/segmentBuilder";
import {
  CAPTURE_ENABLED,
  CAPTURE_SEGMENT_DROP_ENABLED,
} from "../../constants/featureFlags";

/**
 * CaptureSegmentDropProvider — the WIRING that makes Kelly's capture→Segment
 * drop real on the builder page.
 *
 * PR #2499 shipped every part of this feature except the join: the drag source,
 * the drop zone, the picker, the type→block mapping and the craft insert all
 * existed and were tested, and turning the flag on still rendered nothing,
 * because nothing supplied the builder's Segment list or its insert function.
 * That gap was left open ON PURPOSE — the author declined to guess which value
 * identifies the open Segment, since a wrong guess writes the user's capture
 * into a Segment they were not looking at.
 *
 * THE ACCESSOR, AND WHY IT IS THIS ONE (proven from the builder's own code, not
 * inferred from a name):
 *
 *   `getSelectedSegment(state)` -> the `wheel_segments` row with
 *   `selected === true`. Its `.id` is the open Segment because
 *
 *     - `components/SegmentView/index.jsx` KEYS the craft `<Frame>` by that id,
 *       so the ROOT canvas `useInsertCaptureIntoSegment` appends into is that
 *       Segment's canvas, and
 *     - `pages/SegmentBuilder/index.jsx` writes craft's `editorState` back into
 *       the row whose `id === selectedArc.id`, both on every edit and in the
 *       SAVE payload — so a node appended to ROOT persists as that Segment's
 *       `nodes`.
 *
 *   `newhotbar.selectedSegmentId` is deliberately NOT used: it is read by
 *   ViewWheel, never by the builder canvas.
 *
 * Because the ids handed to the picker come from the SAME `wheel_segments`
 * array, the picker's option ids and this accessor cannot drift apart.
 *
 * MOUNTING: must be inside `<Editor>` — `useInsertCaptureIntoSegment` calls
 * craft's `useEditor()`. It renders no DOM of its own, so it cannot affect
 * layout.
 *
 * FLAG-OFF BEHAVIOUR: `isEnabled` false and the inert `place`. The drawer's own
 * `canAddToSegment` gate and the drop zone's `isEnabled` gate both go quiet, so
 * the builder renders exactly as it does today.
 *
 * `onCreateSegment` — the page's own `addSegment` (the "+ Segment" toolbar
 * action), handed down so a wheel with ZERO Segments can create one on demand
 * (ISP-9999 add-to-segment-autocreate) instead of the picker being a dead end.
 * Reusing it, rather than duplicating its id/colour/value math here, is what
 * makes an auto-created Segment indistinguishable from one added by hand.
 */
const CaptureSegmentDropProvider = ({ children, onCreateSegment, onAfterInsert }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const wheelSegments = useSelector(getWheelSegments);
  const selectedArc = useSelector(getSelectedSegment);
  const rawInsert = useInsertCaptureIntoSegment();
  // stgport-add-to-segment-0911: flush the canvas's true current content into
  // redux synchronously after EVERY successful insert (not just outgoing
  // switches) — see `flushCurrentCanvasNow` (pages/SegmentBuilder/index.jsx)
  // for why a plain react-effect-driven write loses a multi-segment batch's
  // earlier targets. `onAfterInsert` is optional so existing callers/tests
  // that don't pass it keep working unchanged.
  const insert = useCallback(
    (item) => {
      const outcome = rawInsert(item);
      if (outcome.status === "ok" && onAfterInsert) onAfterInsert();
      return outcome;
    },
    [rawInsert, onAfterInsert],
  );

  const activeSegmentId = selectedArc?.id ?? null;
  // daniyal-0909-stg-capture-add-to-both-segments-drops-one: a Segment switch
  // re-keys the builder's craft `<Frame>` (SegmentView's ISP-9999 V2 fix), and
  // that remount's own internal node-id bookkeeping does a state update
  // DURING RENDER (craft.js/@craftjs/utils's nanoid dedupe) — a pattern React
  // itself warns about ("Cannot update a component while rendering a
  // different component"). MEASURED (addToBothSegments.integration.test.jsx,
  // a real CaptureSegmentDropProvider + a real identically-keyed `<Frame>`):
  // when that coincides with a switch, THIS component stops receiving further
  // `useSelector` re-renders even though the Redux store itself updates
  // correctly (verified by reading the store directly) — so the
  // `activeSegmentId` handed to `usePendingCaptureInsert` can go stale and its
  // "wait for the switch to land" polling never sees the real value. Handing
  // the hook a way to read the CURRENT selection straight from the store
  // (bypassing this component's own, possibly-stalled subscription) is what
  // lets that poll succeed regardless.
  const getActiveSegmentId = useCallback(
    () => getSelectedSegment(store.getState())?.id ?? null,
    [store]
  );

  const isEnabled = CAPTURE_ENABLED && CAPTURE_SEGMENT_DROP_ENABLED;

  /**
   * Switch the canvas to another Segment, using the app's own idiom
   * (`withHistory(setWheelSegments(...))`) so the move is a real, undoable
   * builder action and not a second, divergent way to change Segments.
   *
   * A target that is not in the list is REFUSED rather than applied: applying
   * it would deselect every Segment and leave the canvas blank, which reads as
   * the builder having crashed.
   */
  const onSelectSegment = useCallback(
    (segmentId) => {
      // stgport-add-to-segment-0911: read the LIVE store, not this
      // component's own `useSelector` snapshot (`wheelSegments`) — that
      // snapshot is exactly what can still be one render behind a just-landed
      // insert into a DIFFERENT segment earlier in the same multi-segment
      // batch (`CapturedOnWheel.handlePlace`'s await loop can reach this
      // call before React has re-rendered this component). Dispatching from
      // a stale snapshot here would silently revert that earlier insert.
      const liveWheelSegments = getWheelSegments(store.getState());
      if (!containsSegmentId(liveWheelSegments, segmentId)) return;
      // DevOps-Staging-prod has no `withHistory`/undo-suppression system (that's
      // a sandbox-only addition, out of Capture's scope) — dispatch directly,
      // matching every other `setWheelSegments` call site in this prod tree
      // (see pages/SegmentBuilder/index.jsx).
      dispatch(setWheelSegments(selectSegmentById(liveWheelSegments, segmentId)));
    },
    [dispatch, store],
  );

  const { place, isPending } = usePendingCaptureInsert({
    activeSegmentId,
    getActiveSegmentId,
    onSelectSegment,
    insert,
  });

  /**
   * Create a brand-new Segment via `onCreateSegment` (the same "+ Segment"
   * logic the toolbar button runs), then place `item` into it.
   *
   * `onCreateSegment` returns the new Segment's id SYNCHRONOUSLY — the redux
   * write it dispatches has not landed yet, so `activeSegmentId` still reads
   * the pre-create value at this point. `place(..., { skipSelect: true })`
   * reuses the exact same "wait for the canvas to really be the target Segment
   * before writing" machinery the picker's cross-Segment placements use,
   * without asking it to re-dispatch a select — the new Segment already lands
   * `selected: true` as part of being created, so there is nothing to switch.
   */
  const createSegmentAndPlace = useCallback(
    (item) => {
      if (!onCreateSegment) {
        return Promise.resolve({
          ok: false,
          message: "Couldn't create a Segment. Please try again.",
        });
      }
      const newSegmentId = onCreateSegment();
      if (newSegmentId === undefined || newSegmentId === null) {
        return Promise.resolve({
          ok: false,
          message: "Couldn't create a Segment. Please try again.",
        });
      }
      return place(item, newSegmentId, { skipSelect: true });
    },
    [onCreateSegment, place],
  );

  const value = useMemo(
    () => ({
      isEnabled,
      wheelSections: Array.isArray(wheelSegments) ? wheelSegments : [],
      activeSegmentId,
      isPlacing: isPending,
      place,
      createSegmentAndPlace,
    }),
    [isEnabled, wheelSegments, activeSegmentId, isPending, place, createSegmentAndPlace],
  );

  return (
    <CaptureSegmentDropContext.Provider value={value}>
      {children}
    </CaptureSegmentDropContext.Provider>
  );
};

export default CaptureSegmentDropProvider;
